import type { Dayjs } from 'dayjs'
import type { ReactNode } from 'react'

import { getNextWeekdays, getOnlyDate, isSameDate } from '~/astra'
import {
    createBatchPractitionerScheduleLocation,
    createBatchPractitionerScheduleStatus,
    deleteBatchPractitionerScheduleLocation,
    deleteBatchPractitionerScheduleStatus,
} from '~/store/di-entity.api'
import { createMinimalLocationScheduleId, createMinimalStatusId, type MinimalLocationSchedule, type MinimalStatus } from '~/store/slices/surgeonSchedulerSlice'
import { type StoreState, useStore } from '~/store/store'
import {
    type Include,
    type PractitionerScheduleLocation,
    type PractitionerScheduleStatus,
    selectPractitionerScheduleLocations,
    selectPractitionerScheduleStatuses,
} from '~/storm'

import type { DatePractitionerCell } from '../../shared'

export type ConfirmFn = (options: { title: string; description: ReactNode; cancelButtonLabel: string; confirmButtonLabel: string }) => Promise<boolean>

type VisibleDate = { date: Dayjs }
type StatusWithDefinition = Include<PractitionerScheduleStatus, { definition: true }>
type LocationWithLocation = Include<PractitionerScheduleLocation, { location: true }>

type StatusForPaste = Pick<PractitionerScheduleStatus, 'id' | 'status_code' | 'practitioner_status_definition_uuid'> & {
    definition?: { color?: string | null; code?: string | null; label?: string | null } | null
}
type LocationForPaste = Pick<PractitionerScheduleLocation, 'id' | 'location_id'> & {
    location?: { room_code?: string | null } | null
}

type StatusCreatePayload = {
    practitioner_id: number
    scheduled_date: string
    status_code: string
    practitioner_status_definition_uuid: string
}

type LocationCreatePayload = {
    practitioner_id: number
    scheduled_date: string
    location_id: number
}

type StatusPastePlan = {
    payloads: StatusCreatePayload[]
    pending: MinimalStatus[]
    pendingIds: string[]
}

type LocationPastePlan = {
    payloads: LocationCreatePayload[]
    pending: MinimalLocationSchedule[]
    pendingIds: string[]
}

export type PastePair = {
    sourceCell: DatePractitionerCell
    targetCell: DatePractitionerCell
}

function cellKey(cell: DatePractitionerCell) {
    return `${getOnlyDate(cell.date)}-${cell.practitionerId}`
}

function getCellAttrs(cells: DatePractitionerCell[]) {
    return cells.map(({ date, practitionerId }) => ({
        scheduled_date: getOnlyDate(date),
        practitioner_id: practitionerId,
    }))
}

function getStatusesForCell(state: StoreState, cell: DatePractitionerCell): StatusWithDefinition[] {
    return selectPractitionerScheduleStatuses(state, {
        where: { scheduled_date: getOnlyDate(cell.date), practitioner_id: cell.practitionerId },
        include: { definition: true },
    })
}

function getLocationsForCell(state: StoreState, cell: DatePractitionerCell): LocationWithLocation[] {
    return selectPractitionerScheduleLocations(state, {
        where: { scheduled_date: getOnlyDate(cell.date), practitioner_id: cell.practitionerId },
        include: { location: true },
    })
}

function getScheduleDataForCells(state: StoreState, cells: DatePractitionerCell[]) {
    const cellAttrs = getCellAttrs(cells)
    if (cellAttrs.length === 0) return { statuses: [], locations: [] }

    return {
        statuses: selectPractitionerScheduleStatuses(state, { where: { OR: cellAttrs } }),
        locations: selectPractitionerScheduleLocations(state, { where: { OR: cellAttrs } }),
    }
}

function deleteIds(ids: number[], deleteFn: (ids: number[]) => Promise<unknown>) {
    if (ids.length === 0) return Promise.resolve()
    return deleteFn(ids)
}

function addClipboardValidationToast() {
    useStore.getState().toaster.actions.addToast({
        color: 'critical',
        message: 'Du kan kun kopiere celler som har samme kirurg.',
    })
}

function isTargetVisible(pair: PastePair, visibleDates: VisibleDate[]) {
    return visibleDates.some(visibleDate => isSameDate(visibleDate.date, pair.targetCell.date))
}

function toPendingStatus(cell: DatePractitionerCell, status: StatusForPaste): MinimalStatus {
    return {
        date: getOnlyDate(cell.date),
        practitionerId: cell.practitionerId,
        color: status.definition?.color ?? '',
        status_code: status.definition?.code ?? status.status_code,
        label: status.definition?.label ?? '',
    }
}

function toPendingLocation(cell: DatePractitionerCell, location: LocationForPaste): MinimalLocationSchedule {
    return {
        date: getOnlyDate(cell.date),
        practitionerId: cell.practitionerId,
        room_code: location.location?.room_code ?? '',
        location_id: location.location_id,
    }
}

async function createPlannedStatuses(plan: StatusPastePlan, actions: StoreState['surgeonScheduler']['actions']) {
    if (plan.payloads.length === 0) return

    actions.addBatchPendingStatus(plan.pending)
    try {
        await createBatchPractitionerScheduleStatus(plan.payloads)
    } finally {
        actions.deleteBatchPendingStatus(plan.pendingIds)
    }
}

async function createPlannedLocations(plan: LocationPastePlan, actions: StoreState['surgeonScheduler']['actions']) {
    if (plan.payloads.length === 0) return

    actions.addBatchPendingLocationSchedule(plan.pending)
    try {
        await createBatchPractitionerScheduleLocation(plan.payloads)
    } finally {
        actions.deleteBatchPendingLocationSchedule(plan.pendingIds)
    }
}

async function deleteScheduleDataForCells(state: StoreState, cells: DatePractitionerCell[]) {
    const { statuses, locations } = getScheduleDataForCells(state, cells)

    await Promise.all([
        deleteIds(
            statuses.map(({ id }) => id),
            deleteBatchPractitionerScheduleStatus
        ),
        deleteIds(
            locations.map(({ id }) => id),
            deleteBatchPractitionerScheduleLocation
        ),
    ])
}

export function cellsHaveSamePractitioner(cells: DatePractitionerCell[]) {
    const practitionerId = cells.at(0)?.practitionerId
    return practitionerId === undefined || cells.every(cell => cell.practitionerId === practitionerId)
}

export function shouldConfirmDelete(statusCount: number, locationCount: number) {
    return statusCount > 2 || locationCount > 2
}

export function formatDeleteTitle(statusCount: number, locationCount: number) {
    const parts = [statusCount > 0 ? `${statusCount} statuser` : '', locationCount > 0 ? `${locationCount} stuer` : ''].filter(Boolean)
    return `Slette ${parts.join(' og ')}?`
}

export function buildPastePairs(sourceCells: DatePractitionerCell[], targetCell: DatePractitionerCell): PastePair[] {
    return getNextWeekdays(targetCell.date, sourceCells.length).map((date, index) => ({
        sourceCell: sourceCells[index]!,
        targetCell: {
            date,
            practitionerId: targetCell.practitionerId,
        },
    }))
}

export function filterVisiblePastePairs(pairs: PastePair[], visibleDates: VisibleDate[]) {
    return pairs.filter(pair => isTargetVisible(pair, visibleDates))
}

export function getHiddenPastePairCount(pairs: PastePair[], visibleDates: VisibleDate[]) {
    return pairs.length - filterVisiblePastePairs(pairs, visibleDates).length
}

export function getPasteOutsideViewDescription(hiddenCount: number) {
    return hiddenCount === 1
        ? 'Du prøver å lime inn en celle som ikke er synlig i denne visningen. Vil du fortsette?'
        : `Du prøver å lime inn ${hiddenCount} celler som ikke er synlige i denne visningen. Vil du fortsette?`
}

export function buildStatusPastePlan(
    pairs: PastePair[],
    getSourceStatuses: (cell: DatePractitionerCell) => StatusForPaste[],
    getTargetStatuses: (cell: DatePractitionerCell) => StatusForPaste[]
): StatusPastePlan {
    const blockedByTarget = new Map<string, Set<string>>()
    const payloads: StatusCreatePayload[] = []
    const pending: MinimalStatus[] = []

    for (const pair of pairs) {
        const targetKey = cellKey(pair.targetCell)
        const blocked =
            blockedByTarget.get(targetKey) ??
            new Set(
                getTargetStatuses(pair.targetCell)
                    .map(status => status.status_code)
                    .filter((statusCode): statusCode is string => Boolean(statusCode))
            )
        blockedByTarget.set(targetKey, blocked)

        for (const status of getSourceStatuses(pair.sourceCell)) {
            if (!status.status_code || blocked.has(status.status_code)) continue

            blocked.add(status.status_code)
            payloads.push({
                practitioner_id: pair.targetCell.practitionerId,
                scheduled_date: getOnlyDate(pair.targetCell.date),
                status_code: status.status_code,
                practitioner_status_definition_uuid: status.practitioner_status_definition_uuid,
            })
            pending.push(toPendingStatus(pair.targetCell, status))
        }
    }

    return {
        payloads,
        pending,
        pendingIds: pending.map(createMinimalStatusId),
    }
}

export function buildLocationPastePlan(
    pairs: PastePair[],
    getSourceLocations: (cell: DatePractitionerCell) => LocationForPaste[],
    getTargetLocations: (cell: DatePractitionerCell) => LocationForPaste[]
): LocationPastePlan {
    const blockedByTarget = new Map<string, Set<number>>()
    const payloads: LocationCreatePayload[] = []
    const pending: MinimalLocationSchedule[] = []

    for (const pair of pairs) {
        const targetKey = cellKey(pair.targetCell)
        const blocked = blockedByTarget.get(targetKey) ?? new Set(getTargetLocations(pair.targetCell).map(location => location.location_id))
        blockedByTarget.set(targetKey, blocked)

        for (const location of getSourceLocations(pair.sourceCell)) {
            if (blocked.has(location.location_id)) continue

            blocked.add(location.location_id)
            payloads.push({
                practitioner_id: pair.targetCell.practitionerId,
                scheduled_date: getOnlyDate(pair.targetCell.date),
                location_id: location.location_id,
            })
            pending.push(toPendingLocation(pair.targetCell, location))
        }
    }

    return {
        payloads,
        pending,
        pendingIds: pending.map(createMinimalLocationScheduleId),
    }
}

export function getCutSourceCells(sourceCells: DatePractitionerCell[], pastedPairs: PastePair[]) {
    const targetKeys = new Set(pastedPairs.map(pair => cellKey(pair.targetCell)))
    return sourceCells.filter(cell => !targetKeys.has(cellKey(cell)))
}

export async function deleteSelectedActivities(cells: DatePractitionerCell[], confirm: ConfirmFn) {
    const state = useStore.getState()
    const { statuses, locations } = getScheduleDataForCells(state, cells)
    if (statuses.length === 0 && locations.length === 0) return

    if (shouldConfirmDelete(statuses.length, locations.length)) {
        const isConfirmed = await confirm({
            title: formatDeleteTitle(statuses.length, locations.length),
            description: 'Aktivitetene og stuer blir borte for alltid.',
            cancelButtonLabel: 'Avbryt',
            confirmButtonLabel: 'Slett',
        })
        if (!isConfirmed) return
    }

    await Promise.all([
        deleteIds(
            statuses.map(({ id }) => id),
            deleteBatchPractitionerScheduleStatus
        ),
        deleteIds(
            locations.map(({ id }) => id),
            deleteBatchPractitionerScheduleLocation
        ),
    ])
}

export function copySelectedCells(cells: DatePractitionerCell[]) {
    if (!cellsHaveSamePractitioner(cells)) {
        addClipboardValidationToast()
        return
    }

    useStore.getState().surgeonScheduler.actions.copyCells(cells)
}

export function cutSelectedCells(cells: DatePractitionerCell[]) {
    if (!cellsHaveSamePractitioner(cells)) {
        addClipboardValidationToast()
        return
    }

    useStore.getState().surgeonScheduler.actions.cutCells(cells)
}

export async function pasteClipboardAtCell(targetCell: DatePractitionerCell, visibleDates: VisibleDate[], confirm: ConfirmFn) {
    const clipboardState = useStore.getState()
    const copiedCells = clipboardState.surgeonScheduler.cellClipboard
    const action = clipboardState.surgeonScheduler.clipboardAction
    if (copiedCells.length === 0) return

    const allPairs = buildPastePairs(copiedCells, targetCell)
    const hiddenCount = getHiddenPastePairCount(allPairs, visibleDates)
    const pairs =
        hiddenCount === 0 ||
        (await confirm({
            title: 'Bekreft innliming',
            description: getPasteOutsideViewDescription(hiddenCount),
            cancelButtonLabel: 'Kun synlige celler',
            confirmButtonLabel: 'Lim inn alle celler',
        }))
            ? allPairs
            : filterVisiblePastePairs(allPairs, visibleDates)
    if (pairs.length === 0) return

    const scheduleState = useStore.getState()
    const actions = scheduleState.surgeonScheduler.actions
    const statusPlan = buildStatusPastePlan(
        pairs,
        cell => getStatusesForCell(scheduleState, cell),
        cell => getStatusesForCell(scheduleState, cell)
    )
    const locationPlan = buildLocationPastePlan(
        pairs,
        cell => getLocationsForCell(scheduleState, cell),
        cell => getLocationsForCell(scheduleState, cell)
    )

    await Promise.all([createPlannedStatuses(statusPlan, actions), createPlannedLocations(locationPlan, actions)])

    if (action === 'cut') {
        await deleteScheduleDataForCells(scheduleState, getCutSourceCells(copiedCells, pairs))
        actions.clearClipboard()
    }
}
